import Immutable from "immutable";
import keyConverter from "services/key-converter";
import recordMerger from "services/record-merger";

export const ExpressionRecord = Immutable.Record({
  id: null,
  active: true,
  bestGuess: null,
  body: null,
  bodySimple: null,
  clientId: null,
  confidence: null,
  chatterId: null,
  conversationId: null,
  created: null,
  origin: null,
  responseId: null,
  updated: null
});

export class ExpressionsState extends Immutable.Record({
  /**
   * @memberof ExpressionsState#
   * @type {Immutable.List<ExpressionRecord>}
   */
  loaded: Immutable.List([]),

  /** @memberof ExpressionsState# */
  lastExpressionId: null,

  /**
   * The number of expressions currently loaded
   * @memberof ExpressionsState#
   */
  loadedCount: 0,

  /** @memberof ExpressionsState# */
  loading: false,

  /** @memberof ExpressionsState# */
  error: false,

  /**
   * The total number of expressions on the response
   * @memberof ExpressionsState
   */
  queryCount: 0
}) {}

export const expressions = (state = new ExpressionsState(), action) => {
  let newExpressions;
  let newExpression;

  switch (action.type) {
    case "GET_EXPRESSIONS_REQUEST":
      return state.merge({
        loading: true,
        error: false
      });

    case "GET_EXPRESSIONS_SUCCESS":
      // falls through

    case "GET_TRAINED_EXPRESSIONS_SUCCESS": {
      let loadedExpressions = state.get("loaded");

      const returnedResponses = action.response.data.expressions.map(expression => (
        new ExpressionRecord(recordMerger(expression, new ExpressionRecord()))
      ));

      if (action.clear) {
        loadedExpressions = loadedExpressions.clear();
      }

      const expressionsArray = loadedExpressions.concat(returnedResponses);
      const lastExpression = expressionsArray.last();

      return state.merge({
        loaded: expressionsArray,
        loadedCount: expressionsArray.filter(expression => !expression.responseId).size,
        loading: false,
        lastExpressionId: lastExpression && lastExpression.id,
        error: false
      });
    }

    case "GET_EXPRESSIONS_FAILURE":
      return state.merge({
        loading: false,
        error: true
      });

    case "GET_TOTAL_EXPRESSIONS_COUNT_SUCCESS":
      return state.set("queryCount", action.response.data.count);

    case "CREATE_EXPRESSION_SUCCESS": {
      const existingExpression = state.loaded.find(
        expression => expression.id === action.response.data.expression._id
      );

      if (existingExpression) {
        return state;
      }

      newExpression = new ExpressionRecord(
        recordMerger(
          action.response.data.expression,
          new ExpressionRecord()
        )
      );

      return state.updateIn(["loaded"], arr => arr.unshift(newExpression));
    }

    case "TRAIN_EXPRESSION_SUCCESS":
      newExpressions = state.loaded.filter(expression => (
        expression.id !== action.response.data.expression._id
      ));

      return state.merge({
        loaded: newExpressions,
        loadedCount: state.loadedCount - 1,
        queryCount: state.queryCount - 1
      });

    case "MOVE_EXPRESSION_SUCCESS": {
      newExpressions = state.loaded.filter(expression => (
        expression.id !== action.response.data.expression._id
      ));

      const newState = state.merge({
        loaded: newExpressions
      });

      newExpression = new ExpressionRecord(
        recordMerger(
          action.response.data.expression,
          new ExpressionRecord()
        )
      );

      return newState.updateIn(["loaded"], arr => arr.unshift(newExpression));
    }

    case "PATCH_EXPRESSION_SUCCESS": {
      const updatedExpressions = Immutable.fromJS(keyConverter(action.response.data)).get("updatedExpressions");
      newExpressions = state.get("loaded");

      // apply each of the updates to the expressions that have changed
      updatedExpressions.forEach((expression) => {
        const exprToUpdateIdx = newExpressions.findIndex(
          expr => expr.get("id") === expression.get("id")
        );
        newExpressions = newExpressions.setIn([exprToUpdateIdx, "body"], expression.get("body"));
      });

      return state.merge({
        loaded: newExpressions
      });
    }

    case "DELETE_EXPRESSION_SUCCESS":
      newExpressions = state.loaded.filter(expression => (
        expression.id !== action.response.data.expression._id
      ));

      return state.merge({
        loaded: newExpressions,
        loadedCount: state.loadedCount - 1,
        queryCount: state.queryCount - 1
      });

    case "DELETE_UNTRAINED_EXPRESSIONS_SUCCESS":
    case "TRAIN_EXPRESSIONS_SUCCESS":
      newExpressions = state.loaded.filter(expression => !action.ids.includes(expression.id));

      return state.merge({
        loaded: newExpressions,
        loadedCount: state.loadedCount - action.ids.length,
        queryCount: state.queryCount - action.ids.length
      });

    case "CREATE_EXPRESSIONS_SUCCESS": {
      newExpressions = Immutable.fromJS(keyConverter(action.response.data.created_expressions));
      const newExpressionsLength = newExpressions.size;

      newExpressions = newExpressions.map(expression => new ExpressionRecord(
        recordMerger(expression, new ExpressionRecord())
      ));

      return state.merge({
        loaded: newExpressions.concat(state.get("loaded")),
        loadedCount: state.loadedCount + newExpressionsLength,
        queryCount: state.queryCount + newExpressionsLength
      });
    }

    case "MOVE_ALL_EXPRESSIONS_SUCCESS": {
      const errors = Immutable.fromJS(keyConverter(action.response.data.errors));

      // filter out expressions that were successfully moved (i.e. not in the error list)
      newExpressions = state.get("loaded").filter(
        expression => errors.find(
          error => error.get("expressionId") === expression.get("id")
        )
      );

      return state.merge({
        loaded: newExpressions,
        loadedCount: errors.size,
        queryCount: errors.size
      });
    }

    case "DELETE_EXPRESSIONS_SUCCESS": {
      const results = Immutable.fromJS(keyConverter(action.response.data)).get("deletedExpressions");
      const movedExpressionIds = results.map(result => result.get("id"));

      // filter out expressions that were moved away
      newExpressions = state.get("loaded").filter(
        expression => !movedExpressionIds.includes(expression.get("id"))
      );

      return state.merge({
        loaded: newExpressions,
        loadedCount: state.loadedCount - results.size,
        queryCount: state.queryCount - results.size
      });
    }

    case "MOVE_EXPRESSIONS_SUCCESS": {
      const results = Immutable.fromJS(keyConverter(action.response.data)).get("updatedExpressions");
      const movedExpressionIds = results.map(result => result.get("id"));

      // filter out expressions that were moved away
      newExpressions = state.get("loaded").filter(
        expression => !movedExpressionIds.includes(expression.get("id"))
      );

      return state.merge({
        loaded: newExpressions,
        loadedCount: state.loadedCount - results.size,
        queryCount: state.queryCount - results.size
      });
    }

    default:
      return state;
  }
};
